/**
 * The polygon flow's project wizard as it is on master, for users without the "drawings" feature
 * flag (NewProjectPage picks it). The editor's canvas and tool bars are shared with the new one.
 * Delete this folder once the flag is on for everyone.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useMediaQuery, useTheme } from '@mui/material';

import { LAYER_NORM_NONE } from '../../constants/layers';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { projectsReducer } from '../../store/reducers/projects.reducer';
import { toastReducer } from '../../store/reducers/toast.reducer';
import { getFolderSelector } from '../../store/selectors/projects.selector';
import { getUploadProgress } from '../../store/selectors/ui.selector';
import { getUser } from '../../store/selectors/user.selector';
import { FileMetadataRequest, FileType } from '../../types/file';
import { Mesh } from '../../types/mesh';
import { NewProjectRequest } from '../../types/project';
import { UnitSystem } from '../../types/util';
import { FileUploadProgress } from '../loading/FileUploadProgress';
import { SimpleScene } from '../model-preview/SimpleScene.tsx';
import { CadBottomToolbar } from '../new-project-cad/CadBottomToolbar';
import { CadCanvas } from '../new-project-cad/CadCanvas';
import { CadCheatSheet } from '../new-project-cad/CadCheatSheet';
import { CadLeftToolbar } from '../new-project-cad/CadLeftToolbar';
import { CadScaleBar } from '../new-project-cad/CadScaleBar';
import { CadScaleDialog } from '../new-project-cad/CadScaleDialog';
import { findOverlappingPolygon } from '../new-project-cad/cadUtils';
import { generateProjectGlb } from '../new-project-cad/generateProjectGlb';
import { parseSVG, simplifyPolygon } from '../new-project-cad/parseSVG';
import { DXFData } from '../new-project/dxf/types';
import { CadPolygonSidebar } from './CadPolygonSidebar';
import { CadSetupPanel } from './CadSetupPanel';
import { parseDXF } from './parseDXF';
import { useCadState } from './useCadState';

const { createProject } = projectsReducer.actions;

export default function CadProjectWizard() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const defaultSettings = useAppSelector(getUser)?.settings;
  const folder = useAppSelector(getFolderSelector);
  const uploadProgress = useAppSelector(getUploadProgress);
  const isUploading = !!uploadProgress;

  // File state
  const [dxfData, setDxfData] = useState<DXFData | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageScale, setImageScale] = useState(0.05);
  const [hasFile, setHasFile] = useState(false);

  // Editor state
  const {
    state,
    addPolygon,
    updatePolygon,
    updatePolygonSilent,
    pushUndoCommand,
    deletePolygon,
    subtractFromPolygon,
    splitFromPolygon,
    setTool,
    setViewMode,
    setDrawingPoints,
    setActivePolygon,
    setProjectName,
    addMeasurementPoint,
    clearMeasurement,
    addScalePoint,
    clearScalePoints,
    setScale,
    setScaleRatio,
    resetState,
    undo,
    redo,
    canUndo,
    canRedo,
    isValid,
  } = useCadState();

  // UI state
  const [showCheatSheet, setShowCheatSheet] = useState(false);
  const [coord, setCoord] = useState({ x: 0, y: 0 });
  const [isCreating, setIsCreating] = useState(false);
  const [highlightedLayer, setHighlightedLayer] = useState<string | null>(null);
  const [frustumWidth, setFrustumWidth] = useState(800);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const captureRef = useRef<(() => string) | null>(null);
  const drawingPointsRef = useRef(state.drawingPoints);
  drawingPointsRef.current = state.drawingPoints;

  // ── File loading handlers ──────────────────────────────────────────────────

  const handleDxfLoaded = useCallback(
    (data: DXFData) => {
      setDxfData(data);
      setImageUrl(null);
      setHasFile(true);
      setScale(1, null); // DXF has its own coordinate system, default 1:1
    },
    [setScale],
  );

  const handleImageLoaded = useCallback(
    (url: string) => {
      setImageUrl(url);
      setDxfData(null);
      setHasFile(true);
      setTool('set-scale'); // auto-activate scale calibration
    },
    [setTool],
  );

  const handleSkipUpload = useCallback(() => {
    setHasFile(true);
    setScale(1, null); // no image = no calibration needed, default 1 unit = 1m
  }, [setScale]);

  const handleImportFile = useCallback(
    (file: File) => {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
      if (ext === 'dxf') {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = parseDXF(e.target?.result as string);
            handleDxfLoaded(data);
          } catch (err) {
            console.error('DXF parse error:', err);
          }
        };
        reader.readAsText(file);
      } else if (ext === 'svg') {
        // Parse SVG as structured data (like DXF), not as an image
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = parseSVG(e.target?.result as string);
            handleDxfLoaded(data);
          } catch (err) {
            console.error('SVG parse error:', err);
            // Fallback: load as image
            const url = URL.createObjectURL(file);
            handleImageLoaded(url);
          }
        };
        reader.readAsText(file);
      } else if (['png', 'jpg', 'jpeg', 'webp', 'bmp', 'gif'].includes(ext)) {
        const url = URL.createObjectURL(file);
        handleImageLoaded(url);
      }
    },
    [handleDxfLoaded, handleImageLoaded],
  );

  // ── Unsaved changes warning ──────────────────────────────────────────────
  // ── Back handler ─────────────────────────────────────────────────────────
  const handleBack = useCallback(() => {
    setDxfData(null);
    setImageUrl(null);
    setHasFile(false);
    resetState();
  }, [resetState]);

  // ── Vertex update handlers ───────────────────────────────────────────────
  const handleUpdatePolygonPoints = useCallback(
    (id: string, points: [number, number][]) => {
      updatePolygon(id, { points });
    },
    [updatePolygon],
  );

  /** Silent update during canvas drag — no undo push per frame. */
  const handleUpdatePolygonPointsSilent = useCallback(
    (id: string, points: [number, number][]) => {
      updatePolygonSilent(id, { points });
    },
    [updatePolygonSilent],
  );

  /** Push a single undo snapshot after a drag completes. */
  const handlePushUndoSnapshot = useCallback(
    (polygonId: string, oldPoints: [number, number][]) => {
      pushUndoCommand({ type: 'UPDATE_POLYGON', polygonId, oldValues: { points: oldPoints } });
    },
    [pushUndoCommand],
  );

  // ── Polygon handlers ───────────────────────────────────────────────────────

  const handlePolygonComplete = useCallback(
    (points: [number, number][], holes?: [number, number][][]) => {
      if (state.tool === 'subtract' || state.tool === 'subtract-circle') {
        const targetIdx = findOverlappingPolygon(state.polygons, points);
        if (targetIdx === -1) {
          dispatch(
            toastReducer.actions.showToast({ message: 'projects.cadProject.subtractNoOverlap', type: 'warning' }),
          );
          setDrawingPoints([]);
          return;
        }
        subtractFromPolygon(state.polygons[targetIdx].id, points);
      } else if (state.tool === 'split') {
        const targetIdx = findOverlappingPolygon(state.polygons, points);
        if (targetIdx === -1) {
          dispatch(
            toastReducer.actions.showToast({ message: 'projects.cadProject.subtractNoOverlap', type: 'warning' }),
          );
          setDrawingPoints([]);
          return;
        }
        splitFromPolygon(state.polygons[targetIdx].id, points);
      } else {
        addPolygon(points, holes);
      }
    },
    [addPolygon, subtractFromPolygon, splitFromPolygon, state.tool, state.polygons, dispatch, setDrawingPoints],
  );

  /** Create polygons from a DXF/SVG layer's closed entities */
  const handleCreatePolygonsFromLayer = useCallback(
    (layerName: string) => {
      if (!dxfData) return;
      const layerEntities = dxfData.entities.filter((e) => e.layer === layerName && e.closed && e.vertices.length >= 3);
      if (layerEntities.length === 0) return;

      // Compute a simplification epsilon based on the overall bounding box
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;
      for (const entity of layerEntities) {
        for (const [x, y] of entity.vertices) {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
      const diag = Math.hypot(maxX - minX, maxY - minY);
      const epsilon = diag * 0.005; // 0.5% of bounding diagonal

      for (const entity of layerEntities) {
        const simplified = simplifyPolygon(entity.vertices, epsilon);
        if (simplified.length >= 3) {
          addPolygon(simplified);
        }
      }
    },
    [dxfData, addPolygon],
  );

  // ── Project creation ───────────────────────────────────────────────────────

  const handleCreateProject = useCallback(async () => {
    if (!isValid || state.polygons.length === 0) return;
    setIsCreating(true);

    try {
      // 1. Scale 2D footprint to meters based on calibrated metersPerUnit.
      //    Heights are entered directly in meters by the user, so they must NOT be scaled.
      const scale = state.metersPerUnit;

      // 2. Capture preview image
      const previewBase64 = captureRef.current?.() ?? '';

      // 2b. Capture reference image (the uploaded plan) as base64 if present.
      //     Also compute the image center in canvas world coords so we can shift the
      //     polygons to share the same origin as the reference image plane on load.
      let referenceImageBase64: string | undefined;
      let modelScale: number | undefined;
      let imageCenterX = 0; // in meters, canvas world coords
      let imageCenterY = 0;
      if (imageUrl) {
        try {
          const res = await fetch(imageUrl);
          const blob = await res.blob();
          const dataUrl = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          // Strip the `data:<mime>;base64,` prefix — backend expects raw base64
          const commaIdx = dataUrl.indexOf(',');
          referenceImageBase64 = commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : dataUrl;
          // Real-world meters represented by one image pixel:
          // image is rendered at `imageScale` in canvas units, and 1 canvas unit = metersPerUnit meters
          modelScale = imageScale * state.metersPerUnit;

          // Read natural dimensions to find image center in canvas world coords.
          // The image is placed with its bottom-left at (0,0); top-right at (W*imageScale, H*imageScale).
          const { naturalW, naturalH } = await new Promise<{ naturalW: number; naturalH: number }>(
            (resolve, reject) => {
              const probe = new Image();
              probe.onload = () => resolve({ naturalW: probe.naturalWidth, naturalH: probe.naturalHeight });
              probe.onerror = reject;
              probe.src = dataUrl;
            },
          );
          imageCenterX = (naturalW * imageScale * scale) / 2;
          imageCenterY = (naturalH * imageScale * scale) / 2;
        } catch (err) {
          console.error('Failed to encode reference image:', err);
        }
      }

      // Shift polygons so the reference-image center sits at world origin.
      // When no image is present, `imageCenterX/Y` stay 0 and `generateProjectGlb`
      // will recenter the geometry as before.
      const scaledPolygons = state.polygons.map((p) => ({
        ...p,
        points: p.points.map(([x, y]) => [x * scale - imageCenterX, y * scale - imageCenterY] as [number, number]),
        holes: p.holes?.map((hole) =>
          hole.map(([x, y]) => [x * scale - imageCenterX, y * scale - imageCenterY] as [number, number]),
        ),
      }));

      // 3. Generate GLB (returns unique mesh names matching GLB node names).
      //    When the reference image defines our origin, do not recenter on X/Z so
      //    the polygons keep their position relative to the image on load.
      const { blob: glbBlob, meshNames } = await generateProjectGlb(scaledPolygons, {
        recenter: !imageUrl,
      });

      // 3. Convert polygons to Mesh objects
      //    Mesh.name must match the GLB mesh node name (polygon.name based)
      //    so taman-ml can correlate GLB geometry with layer metadata.
      const meshes: Mesh[] = state.polygons.map((p, index) => ({
        uuid: p.id,
        name: meshNames[index],
        displayName: p.name,
        color: p.color,
        type: p.type.toUpperCase(),
        normName: p.illuminated && p.norm !== LAYER_NORM_NONE ? p.norm : undefined,
        canHoldLight: p.canHoldLight,
        desiredLuminaireHeight: p.desiredLuminaireHeight,
        isIlluminated: p.illuminated,
      }));

      // 4. Build request
      const glbFileName = 'cad-project.glb';
      const props: FileMetadataRequest[] = [{ name: glbFileName, fileType: FileType.OBJECT_3D }];

      const request: NewProjectRequest = {
        name: state.projectName.trim() || t('common.untitled'),
        description: '',
        tolerance: defaultSettings?.tolerance ?? 0,
        maintenance: defaultSettings?.maintenance ?? 0,
        units: defaultSettings?.units ?? UnitSystem.METRIC,
        latitude: 0,
        longitude: 0,
        previewImageBase64: previewBase64,
        referenceImageBase64,
        modelScale,
        props,
        lights: [],
        meshes,
        meshGroups: [],
        folderId: folder?.id,
      };

      // 5. Build FormData
      const formData = new FormData();
      const json = JSON.stringify(request);
      const blob = new Blob([json], { type: 'application/json' });
      formData.append('project', blob);
      formData.append('models', new File([glbBlob], glbFileName, { type: 'model/gltf-binary' }));

      // 6. Dispatch
      dispatch(createProject(formData));
    } catch (err) {
      console.error('Project creation error:', err);
      setIsCreating(false);
    }
  }, [
    isValid,
    state.polygons,
    state.projectName,
    state.metersPerUnit,
    imageUrl,
    imageScale,
    t,
    defaultSettings,
    folder,
    dispatch,
  ]);

  // ═══════════════════════════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  // ── Keyboard shortcuts for undo/redo ─────────────────────────────────────
  useEffect(() => {
    if (!hasFile) return; // Only active in editor phase
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (!isMod) return;

      // Don't hijack undo/redo inside text inputs
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      if (e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        // During drawing, CadCanvas already handles point undo — only call stack undo otherwise
        if (drawingPointsRef.current.length === 0) undo();
      } else if ((e.key === 'z' && e.shiftKey) || e.key === 'y') {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasFile, undo, redo]);

  // Phase 1: Setup (no file loaded yet)
  if (!hasFile) {
    return (
      <div className="w-full h-[calc(100dvh-64px)] relative overflow-hidden bg-black">
        {/* Background grid placeholder */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: isMobile ? 0 : 455,
            bottom: 0,
            background: 'transparent',
            zIndex: 0,
          }}
        >
          <SimpleScene />
        </div>

        <div style={{ position: 'relative', zIndex: 1, height: '100%', pointerEvents: 'none' }}>
          <CadSetupPanel
            projectName={state.projectName}
            onProjectNameChange={setProjectName}
            onDxfLoaded={handleDxfLoaded}
            onImageLoaded={handleImageLoaded}
            onSkip={handleSkipUpload}
          />
        </div>
      </div>
    );
  }

  // Phase 2: Editor (file loaded)
  return (
    <div className="w-full h-[calc(100dvh-64px)] relative overflow-hidden bg-[#111827]">
      {isUploading && <FileUploadProgress uploadProgress={uploadProgress} />}

      {/* Canvas area */}
      <div
        ref={canvasContainerRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: isMobile ? 0 : 455,
          bottom: 0,
          zIndex: 0,
        }}
      >
        <CadCanvas
          dxfData={dxfData}
          imageUrl={imageUrl}
          imageScale={imageScale}
          polygons={state.polygons}
          activePolygonIndex={state.activePolygonIndex}
          drawingPoints={state.drawingPoints}
          tool={state.tool}
          viewMode={state.viewMode}
          snapEnabled={state.snapEnabled}
          gridSize={state.gridSize}
          highlightedLayer={highlightedLayer}
          onPolygonComplete={handlePolygonComplete}
          onDrawingPointsChange={setDrawingPoints}
          onCoordChange={setCoord}
          onPolygonSelect={setActivePolygon}
          onUpdatePolygonPoints={handleUpdatePolygonPoints}
          onUpdatePolygonPointsSilent={handleUpdatePolygonPointsSilent}
          onPushUndoSnapshot={handlePushUndoSnapshot}
          captureRef={captureRef}
          onZoomChange={setFrustumWidth}
          metersPerUnit={state.metersPerUnit}
          measurementPoints={state.measurementPoints}
          onAddMeasurementPoint={addMeasurementPoint}
          onClearMeasurement={clearMeasurement}
          onImageScaleChange={setImageScale}
          scalePoints={state.scalePoints}
          scaleSet={state.scaleSet}
          onAddScalePoint={addScalePoint}
          onClearScalePoints={clearScalePoints}
        />

        {/* Split button — above bottom toolbar when split tool active with 3+ points */}
        {state.viewMode === '2d' && state.tool === 'split' && state.drawingPoints.length >= 3 && (
          <div className="absolute bottom-[82px] left-1/2 -translate-x-1/2 z-50">
            <button
              onClick={() => handlePolygonComplete(state.drawingPoints)}
              className="px-5 py-2.5 rounded-xl text-xs font-semibold bg-orange-500 text-white hover:bg-orange-600 transition-colors shadow-lg"
            >
              Split now (or press Enter)
            </button>
          </div>
        )}

        {/* Bottom toolbar */}
        <CadBottomToolbar
          viewMode={state.viewMode}
          scaleSet={state.scaleSet}
          metersPerUnit={state.metersPerUnit}
          scaleRatio={state.scaleRatio}
          tool={state.tool}
          onViewModeChange={setViewMode}
          onSetScaleTool={() => setTool('set-scale')}
          onScaleRatioChange={(mpu, ratio) => setScaleRatio(mpu, ratio)}
          onImportFile={handleImportFile}
          onUndo={() => {
            if (state.drawingPoints.length > 0) {
              setDrawingPoints(state.drawingPoints.slice(0, -1));
            } else {
              undo();
            }
          }}
          onRedo={redo}
          canUndo={state.drawingPoints.length > 0 || canUndo}
          canRedo={canRedo}
        />

        {/* Coordinates — inline left of bottom toolbar, vertically centered */}
        {state.viewMode === '2d' && (
          <div className="absolute bottom-[30px] left-[calc(50%+180px)] mr-3 px-3 py-1.5 bg-[#0a0e14]/90 border border-white/10 rounded-lg text-[11px] font-mono text-gray-400 z-50 pointer-events-none whitespace-nowrap">
            X:<span className="text-[#d4ff33] ml-0.5">{coord.x.toFixed(2)}</span> Y:
            <span className="text-[#d4ff33] ml-0.5">{coord.y.toFixed(2)}</span>
            <span className="text-gray-400 ml-1.5">
              ({state.scaleRatio ?? `${parseFloat(state.metersPerUnit.toFixed(2))} m/u`})
            </span>
          </div>
        )}

        {/* Hint shown while drawing a polygon, once there are enough points to create one */}
        {state.viewMode === '2d' && state.tool === 'draw' && state.drawingPoints.length > 2 && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 px-6 py-3 bg-[#0a0e14]/90 border border-blue-400/30 rounded-xl text-lg text-blue-300 z-50 pointer-events-none whitespace-nowrap">
            {t('projects.cadProject.drawPolygonEnterHint')}
          </div>
        )}

        {/* Tool instruction hints */}
        {state.viewMode === '2d' && state.tool === 'circle' && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 px-6 py-3 bg-[#0a0e14]/90 border border-blue-400/30 rounded-xl text-lg text-blue-300 z-50 pointer-events-none whitespace-nowrap">
            {t('projects.cadProject.circleHintCenter')}
          </div>
        )}
        {state.viewMode === '2d' && state.tool === 'roundabout' && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 px-6 py-3 bg-[#0a0e14]/90 border border-blue-400/30 rounded-xl text-lg text-blue-300 z-50 pointer-events-none whitespace-nowrap">
            {state.drawingPoints.length === 0
              ? t('projects.cadProject.roundaboutHintCenter')
              : state.drawingPoints.length === 1
                ? t('projects.cadProject.roundaboutHintInner')
                : t('projects.cadProject.roundaboutHintOuter')}
          </div>
        )}
        {state.viewMode === '2d' && state.tool === 'split' && state.drawingPoints.length === 0 && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 px-6 py-3 bg-[#0a0e14]/90 border border-orange-400/30 rounded-xl text-lg text-orange-300 z-50 pointer-events-none whitespace-nowrap">
            Draw a cut shape over a polygon, then press Enter or click &quot;Split now&quot;
          </div>
        )}
        {state.viewMode === '2d' && state.tool === 'subtract' && state.drawingPoints.length === 0 && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 px-6 py-3 bg-[#0a0e14]/90 border border-orange-400/30 rounded-xl text-lg text-orange-300 z-50 pointer-events-none whitespace-nowrap">
            {t('projects.cadProject.subtractHint')}
          </div>
        )}
        {state.viewMode === '2d' && state.tool === 'subtract-circle' && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 px-6 py-3 bg-[#0a0e14]/90 border border-orange-400/30 rounded-xl text-lg text-orange-300 z-50 pointer-events-none whitespace-nowrap">
            {t('projects.cadProject.subtractCircleHint')}
          </div>
        )}
        {/* Set Scale hints */}
        {state.viewMode === '2d' && state.tool === 'set-scale' && state.scalePoints.length === 0 && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 px-6 py-3 bg-[#0a0e14]/90 border border-red-400/30 rounded-xl text-lg text-red-300 z-50 pointer-events-none whitespace-nowrap">
            {t('projects.cadProject.setScaleHint1', 'Click to place the first reference point on a known dimension')}
          </div>
        )}
        {state.viewMode === '2d' && state.tool === 'set-scale' && state.scalePoints.length === 1 && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 px-6 py-3 bg-[#0a0e14]/90 border border-red-400/30 rounded-xl text-lg text-red-300 z-50 pointer-events-none whitespace-nowrap">
            {t('projects.cadProject.setScaleHint2', 'Click to place the second point')}
          </div>
        )}
        {/* Scale distance input dialog */}
        {state.tool === 'set-scale' && state.scalePoints.length === 2 && (
          <CadScaleDialog
            pixelDistance={Math.hypot(
              state.scalePoints[1][0] - state.scalePoints[0][0],
              state.scalePoints[1][1] - state.scalePoints[0][1],
            )}
            onConfirm={(realDistanceMeters) => {
              const pixelDist = Math.hypot(
                state.scalePoints[1][0] - state.scalePoints[0][0],
                state.scalePoints[1][1] - state.scalePoints[0][1],
              );
              const mpu = pixelDist > 0 ? realDistanceMeters / pixelDist : 1;
              setScale(mpu, null);
            }}
            onCancel={clearScalePoints}
          />
        )}

        {/* Scale bar */}
        {state.viewMode === '2d' && (
          <CadScaleBar
            frustumWidth={frustumWidth}
            canvasWidth={canvasContainerRef.current?.clientWidth ?? 800}
            metersPerUnit={state.metersPerUnit}
          />
        )}

        {/* 3D mode indicator */}
        {state.viewMode === '3d' && (
          <div className="absolute bottom-[82px] left-1/2 -translate-x-1/2 px-3 py-1.5 bg-[#0a0e14]/90 border border-green03 rounded-lg text-xs text-green02 z-50 pointer-events-none">
            {t('projects.cadProject.view3DHint', '3D View — drag: orbit, scroll: zoom')}
          </div>
        )}
      </div>

      {/* Left toolbar */}
      <CadLeftToolbar
        tool={state.tool}
        onToolChange={setTool}
        onCheatSheetToggle={() => setShowCheatSheet(!showCheatSheet)}
        scaleSet={state.scaleSet}
        hasImage={!!imageUrl}
      />

      {/* Right sidebar */}
      <div style={{ position: 'relative', zIndex: 1, height: '100%', pointerEvents: 'none' }}>
        <CadPolygonSidebar
          projectName={state.projectName}
          onProjectNameChange={setProjectName}
          polygons={state.polygons}
          activePolygonIndex={state.activePolygonIndex}
          drawingPointsCount={state.drawingPoints.length}
          isValid={isValid}
          onUpdatePolygon={updatePolygon}
          onDeletePolygon={deletePolygon}
          onSelectPolygon={setActivePolygon}
          onCreateProject={handleCreateProject}
          onBack={handleBack}
          isCreating={isCreating}
          importedLayers={dxfData?.layers ?? null}
          importedEntities={dxfData?.entities ?? null}
          onCreatePolygonsFromLayer={handleCreatePolygonsFromLayer}
          onLayerHover={setHighlightedLayer}
        />
      </div>

      {/* CheatSheet */}
      {showCheatSheet && <CadCheatSheet onClose={() => setShowCheatSheet(false)} />}
    </div>
  );
}
